<template>
    <b-modal
        id="dlgUploadFile"
        ref="uploadFileDialog"
        :title="modalTitle"
        :ok-title="saveButtonTitle"
        :cancel-title="$t('general.cancel')"
        cancel-variant="secondary"
        header-bg-variant="secondary"
        header-text-variant="white"
        centered
        no-fade
        no-close-on-backdrop
        no-close-on-esc
        hide-header-close
        :ok-disabled="disableSaveButton"
        :busy="isLoading || uploadInProgress"
        @ok.prevent="onOkClicked()"
        @cancel="onCancelClicked">
        <div v-if="revisionInfo">
            <b-alert show variant="info" v-if="!revisionInfo.downloaded && revisionInfo.modified && isReplaceMode">
                <b-media no-body>
                    <b-media-aside vertical-align="center">
                        <icon name="info-circle" scale="2"></icon>
                    </b-media-aside>
                    <b-media-body class="ml-3">
                        <small class="m-0 pt-3 pb-3">{{ $t('project.settings.files.editDialog.firstRevisionWarning') }}</small>
                    </b-media-body>
                </b-media>
            </b-alert>
            <b-alert show variant="warning" v-if="revisionInfo.downloaded && revisionInfo.modified && isReplaceMode">
                <b-media no-body>
                    <b-media-aside vertical-align="center">
                        <icon name="warning" scale="2"></icon>
                    </b-media-aside>
                    <b-media-body class="ml-3">
                        <small class="m-0 pt-3 pb-3">{{ $t('project.settings.files.editDialog.secondRevisionWarning') }}</small>
                    </b-media-body>
                </b-media>
            </b-alert>
        </div>
        <b-form novalidate>
            <b-form-group v-if="inputLimits && (isRenameMode || isUploadMode)">
                <template slot="label">
                    <b-row>
                        <b-col cols="9">{{ $t('project.settings.files.uploadDialog.fieldTitle') }}: *</b-col>
                        <b-col cols="3" class="text-right">
                            <small>
                                <b-badge>{{indexData.title.length}} / {{inputLimits.fileTitle}}</b-badge>
                            </small>
                        </b-col>
                    </b-row>
                </template>
                <b-form-input
                    name="txtTitle"
                    v-validate="'required'"
                    type="text"
                    :disabled="!indexData.acl.changeIndex && isRenameMode"
                    v-model="indexData.title"
                    :maxlength="inputLimits.fileTitle"
                    :state="!veeErrors.has('txtTitle')">
                </b-form-input>
                <span v-show="veeErrors.has('txtTitle')" style="color: red;" class="small">{{ $t('project.settings.files.uploadDialog.messages.validation.title') }}</span>
            </b-form-group>
            <b-form-group v-if="inputLimits && (isRenameMode || isUploadMode)">
                <template slot="label">
                    <b-row>
                        <b-col cols="9">{{ $t('project.settings.files.uploadDialog.fieldInfo') }}:</b-col>
                        <b-col cols="3" class="text-right">
                            <small>
                                <b-badge>{{indexData.info.length}} / {{inputLimits.fileInfo}}</b-badge>
                            </small>
                        </b-col>
                    </b-row>
                </template>
                <b-form-input
                    name="txtInfo"
                    type="text"
                    :maxlength="inputLimits.fileInfo"
                    :disabled="!indexData.acl.changeIndex && isRenameMode"
                    v-model="indexData.info">
                </b-form-input>
            </b-form-group>
            <b-form-group v-if="isUploadMode || isReplaceMode">
                <label class="mb-0">{{ $t('project.settings.files.uploadDialog.fieldFile') }} *</label>
                <!-- <b-form-group v-show="!newFileUploaded">
                    <b-row>
                        <b-col cols="4">
                            <document-thumbnail v-if="isReplaceMode" 
                                                :document-id="indexData.id"
                                                :is-clickable="false">
                            </document-thumbnail>
                        </b-col>
                    </b-row>
                </b-form-group> -->
                <vue-dropzone v-if="(indexData.acl.changeFile && isReplaceMode) || isUploadMode"
                    name="dropzoneUploadFile" 
                    ref="refDzUploadFile"
                    id="dropzone"
                    :options="dropzoneOptions"
                    @vdropzone-success="fileUploaded"
                    @vdropzone-removed-file="fileRemoved"
                    @vdropzone-error="failed"
                    @vdropzone-max-files-exceeded="maxFilesReached">
                </vue-dropzone>
                <b-alert v-else show variant="info">
                    <b-media no-body>
                        <b-media-aside vertical-align="center">
                            <icon name="info-circle" scale="2.2"></icon>
                        </b-media-aside>
                        <b-media-body class="ml-3">
                            <small class="m-0">{{ $t('project.settings.files.editDialog.fileNotEditable') }}</small>
                        </b-media-body>
                    </b-media>
                </b-alert>
            </b-form-group>
            <b-form-group v-if="isLoading && (isUploadMode || isReplaceMode)" class="mb-0">
                <b-progress height="10px">
                    <b-progress-bar :value="100" variant="secondary" animated>
                        <small v-text="$t('project.settings.files.uploadInProgress')"></small>
                    </b-progress-bar>
                </b-progress>
            </b-form-group>
        </b-form>
    </b-modal>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import toastr from 'toastr';
import i18n from '@/i18n.bootstrap';
import DocumentThumbnail from '../../Shared/DocumentThumbnail';

export default {
    name: 'upload-file-modal',

    props: ['root', 'successCallback', 'fileRevisionInfo'],

    components: {
        DocumentThumbnail
    },

    data() {
        return {
            indexData: {
                acl: {},
                title: '',
                info: '',
                location: this.root,
                id: null,
                objectTypeId: null,
                colabType: null,
            },

            dropzoneOptions: {
                url: '/colab/epr/projects/' + this.$route.params.projectId + '/file/',
                thumbnailWidth: 200,
                maxFilesize: 0, //  ~2gb
                timeout: null,
                addRemoveLinks: true,
                dictDefaultMessage: i18n.t('project.settings.files.uploadDialog.dropzoneText'),
                dictCancelUpload: i18n.t('project.settings.files.uploadDialog.cancelUploadText'),
                dictRemoveFile: i18n.t('project.settings.files.uploadDialog.removeFileText'),
                maxFiles: 1,
                accept: (file, done) => {
                    if(file.size > 0) {
                        done();
                    } else {
                        done(i18n.t('project.settings.files.zeroSizeFile'));
                        toastr.warning(i18n.t('project.settings.files.zeroSizeFile'));
                    }
                },
                complete: (file) => {
                    let hiddenEl = undefined;
                    for (let i = 0; i < file.previewElement.children.length; i++) {
                        hiddenEl = file.previewElement.children[i];
                        if (hiddenEl.className === 'dz-progress') {
                            hiddenEl.hidden = true;
                            break;
                        }
                    }
                    this.uploadInProgress = true;
                },
                queuecomplete: () => {
                    this.uploadInProgress = false;
                }
            },

            uploadedFileInfo: null,

            isEditMode: false,
            isUploadMode: false,
            isRenameMode: false,
            isReplaceMode: false,
            newFileUploaded: false,
            uploadInProgress: false
        }
    },

    computed: {
        ...mapGetters({
            projectData: 'projects/projectData',
            globalConfig: 'system/globalConfig',
        }),

        ...mapState('system', ['isLoading', 'inputLimits']),

        modalTitle() {
            let dialogType;
            if (this.isRenameMode) {
                dialogType = 'renameDialog'
            } else if(this.isReplaceMode) {
                dialogType = 'replaceDialog'
            } else {
                dialogType = 'uploadDialog'
            }
            return i18n.t(`project.settings.files.${dialogType}.title`);
        },
        saveButtonTitle() {
            let dialogType;
            if (this.isRenameMode) {
                dialogType = 'renameDialog'
            } else if(this.isReplaceMode) {
                dialogType = 'replaceDialog'
            } else {
                dialogType = 'uploadDialog'
            }
            return i18n.t(`project.settings.files.${dialogType}.send`);
        },

        revisionInfo() {
            return this.fileRevisionInfo
        },

        disableSaveButton() {
            return (!this.isEditMode && (this.indexData.title.length === 0 || !this.newFileUploaded)) || 
                   (this.isReplaceMode && !this.newFileUploaded) ||
                   (this.isRenameMode && this.indexData.title.length === 0)
        }
    },

    mounted() {
        this.$store.dispatch('system/loadInputLimits');
        this.$store.dispatch('system/loadGlobalConfiguration').then(response => {
          this.dropzoneOptions.maxFilesize = response.maxFileSize;
        })
        this.$root.$on('bv::show::modal', (modalId, file, isRenameMode, isReplaceMode) => {
            if (file && isRenameMode) {
                this.isEditMode = true;
                this.isUploadMode = false;
                this.isRenameMode = true;
                this.isReplaceMode = false;
                this.indexData.acl = file.acl;
                this.indexData.title = file.title;
                this.indexData.info = file.info;
                this.indexData.id = file.id;
                this.indexData.objectTypeId = file.objectTypeId;
                this.indexData.colabType = file.colabType;
            } else if (file && isReplaceMode) {
                this.isEditMode = true;
                this.isUploadMode = false;
                this.isRenameMode = false;
                this.isReplaceMode = true;
                this.indexData.acl = file.acl;
                this.indexData.title = file.title;
                this.indexData.info = file.info;
                this.indexData.id = file.id;
                this.indexData.objectTypeId = file.objectTypeId;
                this.indexData.colabType = file.colabType;
            } else {
                this.isUploadMode = true;
                this.isEditMode = false;
                this.isRenameMode = false;
                this.isReplaceMode = false;
            }
        });
    },

    watch: {
        root(newVal) {
            if (newVal) {
                this.indexData.location = newVal;
            }
        }
    },

    methods: {
        maxFilesReached(file) {
            this.$refs.refDzUploadFile.removeFile(file);
            toastr.warning(i18n.t('project.settings.files.uploadDialog.messages.validation.maxFilesReached'));
        },

        fileUploaded(file, response) {
            this.newFileUploaded = true;
            this.uploadedFileInfo = response;
            this.$root.$emit('reloadThumbnail', false);
            this.$refs.refDzUploadFile.disable();
        },

        fileRemoved() {
            this.newFileUploaded = false;
            this.uploadedFileInfo = null;
            this.$root.$emit('reloadThumbnail', true);
            this.$refs.refDzUploadFile.enable();
        },

        failed(_file, _message, xhr) {
          if(JSON.parse(xhr.response)[0].message === 'MAXIMUM_UPLOAD_SIZE_EXCEEDED'){
            toastr.error(i18n.t('project.settings.files.uploadDialog.messages.maxUploadSizeExceeded') + '' +this.globalConfig.maxFileSize/1000000+ ' MB');
            _file.previewElement.querySelectorAll('.dz-error-message span')[0].textContent = i18n.t('project.settings.files.uploadDialog.messages.maxUploadSizeExceeded') + '' +this.globalConfig.maxFileSize/1000000+ ' MB';
          }
        },

        resetForm() {
            if(this.indexData.acl.changeFile && !this.isRenameMode) this.$refs.refDzUploadFile.removeAllFiles();
            
            this.isEditMode = false;
            this.isUploadMode = false;
            this.isReplaceMode = false;
            this.isRenameMode = false;

            this.indexData.title = '';
            this.indexData.info = '';
            this.indexData.id = null;
            this.indexData.objectTypeId = null;
            this.indexData.colabType = null;

            this.$validator.reset();
        },

        onCancelClicked() {
            this.resetForm();
        },

        onOkClicked() {
            return this.isEditMode ? this.editFile() : this.uploadNewFile();
        },

        editFile() {
            let dialogType = this.isRenameMode ? 'renameDialog' : 'replaceDialog';
            this.$store.dispatch('system/setLoadingStatus', true);
            this.$validator.validateAll().then(result => {
                if (result) {
                    this.$store.dispatch('projects/editFile', {
                        indexData: this.indexData,
                        fileId: this.uploadedFileInfo ? this.uploadedFileInfo.id : 'file_not_changed',
                        projectId: this.$route.params.projectId,
                        callback: this.successCallback
                    }).then(() => {
                        this.resetForm();
                        this.$root.$emit('bv::hide::modal', 'dlgUploadFile');
                        this.$root.$emit('reloadThumbnail', true);
                        this.$store.dispatch('system/setLoadingStatus', false);
                        toastr.success(i18n.t(`project.settings.files.${dialogType}.messages.success`));
                    }).catch(() => {
                        this.$store.dispatch('system/setLoadingStatus', false);
                        toastr.error(i18n.t(`project.settings.files.${dialogType}.messages.error`));
                    });
                } else {
                    // toastr.warning(i18n.t('project.settings.files.editDialog.messages.validation.file'));
                    this.$store.dispatch('system/setLoadingStatus', false);
                }
            });
        },

        uploadNewFile() {
            this.$store.dispatch('system/setLoadingStatus', true);
            this.$validator.validateAll().then(result => {
                if (result && this.uploadedFileInfo) {
                    this.$store.dispatch('projects/uploadFile', {
                        indexData: this.indexData,
                        fileId: this.uploadedFileInfo.id,
                        projectId: this.$route.params.projectId,
                        callback: this.successCallback
                    }).then(() => {
                        this.resetForm();
                        this.$root.$emit('bv::hide::modal', 'dlgUploadFile');
                        this.$store.dispatch('system/setLoadingStatus', false);
                    }).catch((error) => {
                        if (error.response.data.type === 'MISSING_MAPPING_CONFIGURATION') {
                            toastr.error(i18n.t('project.settings.files.uploadDialog.messages.errorConfig'));
                        } else if(error.response.data.type === 'COLAB_TYPE_NOT_ALLOWED') {
                            toastr.error(i18n.t('project.settings.files.uploadDialog.messages.colabTypeNotAllowed'));
                        }else if(error.response.data.message === 'MAXIMUM_UPLOAD_SIZE_EXCEEDED') {
                            toastr.error(i18n.t('project.settings.files.uploadDialog.messages.maxUploadSizeExceeded'));
                        } else {
                            toastr.error(i18n.t('project.settings.files.uploadDialog.messages.errorWhileUploading'));
                        }
                        this.$store.dispatch('system/setLoadingStatus', false);
                    });
                } else {
                    toastr.warning(i18n.t('project.settings.files.uploadDialog.messages.validation.file'));
                    this.$store.dispatch('system/setLoadingStatus', false);
                }
            });
        }
    }
}
</script>

<style scoped>
</style>
